import React, { useEffect, useState } from "react";
import { BarChart3, Download } from "lucide-react";
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend,
} from "recharts";
import api, { taka } from "@/lib/api";
import { Th, Td, Empty } from "@/pages/admin/AdminUI";

const COLORS = ["#1E3A2B", "#CA8A04", "#059669", "#DC2626", "#6366F1", "#0891B2", "#D97706", "#7C3AED", "#0D9488", "#B91C1C"];

export default function AdminReports() {
  const [profit, setProfit] = useState(null);
  const [productSales, setProductSales] = useState([]);

  useEffect(() => {
    api.get("/admin/reports/profit").then((r) => setProfit(r.data)).catch(() => {});
    api.get("/admin/reports/product-sales").then((r) => setProductSales(r.data)).catch(() => {});
  }, []);

  const exportCSV = () => {
    const rows = [["পণ্য", "পরিমাণ", "আয়"], ...productSales.map((p) => [p.product, p.qty, p.revenue])];
    const csv = rows.map((r) => r.join(",")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "product-sales-report.csv"; a.click();
  };

  if (!profit) return <div className="text-stone-500">লোড হচ্ছে...</div>;

  const periods = [["daily", "দৈনিক"], ["weekly", "সাপ্তাহিক"], ["monthly", "মাসিক"], ["yearly", "বার্ষিক"]];

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-[#1E3A2B] flex items-center gap-2"><BarChart3 /> রিপোর্ট ও লাভ-ক্ষতি</h1>
        <div className="flex gap-2">
          <button onClick={exportCSV} className="border border-stone-300 bg-white h-11 px-4 rounded-xl flex items-center gap-2 font-medium" data-testid="export-csv"><Download size={16} /> CSV</button>
          <button onClick={() => window.print()} className="border border-stone-300 bg-white h-11 px-4 rounded-xl flex items-center gap-2 font-medium" data-testid="print-report"><Download size={16} /> প্রিন্ট</button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {periods.map(([k, l]) => (
          <div key={k} className="bg-white rounded-2xl border border-stone-200 p-4" data-testid={`profit-${k}`}>
            <div className="text-sm text-stone-500">{l} লাভ</div>
            <div className={`text-xl font-bold ${profit[k].profit >= 0 ? "text-emerald-700" : "text-red-600"}`}>{taka(profit[k].profit)}</div>
            <div className="text-xs text-stone-400">বিক্রি {taka(profit[k].sales)} • খরচ {taka(profit[k].expense)}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="bg-[#1E3A2B] text-white rounded-2xl p-5"><div className="text-emerald-200 text-sm">মোট বিক্রি</div><div className="text-2xl font-bold">{taka(profit.total_sales)}</div></div>
        <div className="bg-white rounded-2xl border border-stone-200 p-5"><div className="text-stone-500 text-sm">মোট খরচ</div><div className="text-2xl font-bold text-red-600">{taka(profit.total_expense)}</div></div>
        <div className="bg-[#CA8A04] text-white rounded-2xl p-5"><div className="text-amber-100 text-sm">নিট লাভ</div><div className="text-2xl font-bold">{taka(profit.net_profit)}</div></div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-stone-200 p-5">
          <h3 className="font-bold text-[#1E3A2B] mb-4">খরচের বিভাজন</h3>
          {profit.expense_breakdown.length === 0 ? <Empty /> : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={profit.expense_breakdown} dataKey="amount" nameKey="category" cx="50%" cy="50%" outerRadius={90} label={(e) => e.category}>
                  {profit.expense_breakdown.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => taka(v)} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="bg-white rounded-2xl border border-stone-200 p-5">
          <h3 className="font-bold text-[#1E3A2B] mb-4">পণ্যভিত্তিক বিক্রি (আয়)</h3>
          {productSales.length === 0 ? <Empty text="এখনো কোনো বিক্রয় নেই" /> : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={productSales.slice(0, 6)} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis type="number" fontSize={11} /><YAxis type="category" dataKey="product" width={90} fontSize={10} />
                <Tooltip formatter={(v) => taka(v)} />
                <Bar dataKey="revenue" fill="#1E3A2B" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-stone-200 overflow-x-auto">
        <div className="p-4 font-bold text-[#1E3A2B]">পণ্যভিত্তিক বিক্রয় রিপোর্ট</div>
        <table className="w-full min-w-[480px]">
          <thead className="bg-stone-50 border-y border-stone-200"><tr><Th>পণ্য</Th><Th>বিক্রিত পরিমাণ</Th><Th>মোট আয়</Th></tr></thead>
          <tbody className="divide-y divide-stone-100">
            {productSales.map((p) => <tr key={p.product}><Td className="font-medium">{p.product}</Td><Td>{p.qty}</Td><Td className="font-semibold text-emerald-800">{taka(p.revenue)}</Td></tr>)}
          </tbody>
        </table>
        {productSales.length === 0 && <Empty text="এখনো কোনো বিক্রয় নেই" />}
      </div>
    </div>
  );
}
